/**
 * Column length — the ONE bytes→characters conversion for display.
 *
 * SQL Server's `sys.columns.max_length` is a **byte** count: 2 bytes per
 * character for the national types (`nvarchar`/`nchar`), and `-1` for the
 * `(MAX)` forms. That byte value is the app's internal canon — the fetcher
 * (`electron/.../mssqlQueries.ts`), `metadataService`, the Schema Compare
 * snapshot, the column comparer and the Database Blueprint DDL parser (which
 * deliberately multiplies a parsed `NVARCHAR(250)` back to `500`) all agree on
 * it. Nothing here changes that.
 *
 * What DOES have to change per surface is what a human reads. Before this
 * module the halving rule was copy-pasted into the DDL/script generators and
 * simply missing from the Summary tab, the Overview detail dialog, the Object
 * Explorer column labels and the MCP column tools — so Schema Compare could
 * (and did, Soporte round 7 / F1) show `max length: 500 → 100` next to a DDL
 * diff reading `NVARCHAR(250)` → `NVARCHAR(50)` for the very same column.
 *
 * Every DISPLAY site converts through `displayColumnLength` so they cannot
 * disagree again. Producers (fetchers, snapshots, comparers) must NOT use it.
 *
 * Other engines already deliver characters (PostgreSQL derives them from
 * `atttypmod`, MySQL reads `CHARACTER_MAXIMUM_LENGTH`), so the halving is
 * `mssql`-only. Oracle reports `DATA_LENGTH` bytes and has the same class of
 * defect, but fixing it means changing EMITTED DDL (not just display) in
 * lockstep with the blueprint parser — tracked separately, deliberately not
 * handled here.
 */

import type { DatabaseEngineId } from './engineTypes';

/**
 * Types whose parenthesised argument is a LENGTH (as opposed to a precision).
 * Same list — and same uppercase comparison — the DDL generator uses, so the
 * DDL tab and every display surface classify a type identically.
 */
const LENGTH_DATA_TYPES = ['VARCHAR', 'NVARCHAR', 'CHAR', 'NCHAR', 'BINARY', 'VARBINARY'];

/**
 * `number` — the length to print, in CHARACTERS.
 * `'MAX'`  — the unbounded form (`max_length` of `-1`, or an absent length on
 *            a length-carrying type that the producer left null).
 * `null`   — nothing to print: either the type takes no length at all, or the
 *            producer gave us no usable length (undefined / 0 / a negative
 *            other than -1).
 */
export type DisplayColumnLength = number | 'MAX' | null;

/** True when `dataType`'s parenthesised argument is a length, not a precision. */
export function isLengthDataType(dataType: string | null | undefined): boolean {
  if (!dataType) return false;
  return LENGTH_DATA_TYPES.includes(dataType.toUpperCase());
}

/**
 * Convert a stored `maxLength` into the length a user should see.
 *
 * @param engine    the engine the column was read from — only `'mssql'` stores
 *                  bytes, so only it is halved. Undefined (engine not yet
 *                  resolvable on the surface) means "don't convert", which
 *                  keeps today's behaviour rather than guessing.
 * @param dataType  the column's OWN type on THAT side of a comparison — an
 *                  `nvarchar → varchar` change converts differently per side.
 * @param maxLength the stored value (bytes on mssql, characters elsewhere).
 */
export function displayColumnLength(
  engine: DatabaseEngineId | undefined,
  dataType: string | null | undefined,
  maxLength: number | null | undefined
): DisplayColumnLength {
  if (!isLengthDataType(dataType)) return null;
  if (maxLength === null || maxLength === -1) return 'MAX';
  if (maxLength === undefined || maxLength <= 0) return null;

  // `> 1` guard, kept from the DDL generator: a 1-byte value on an N-type is
  // never a real 0.5-character column, it's a producer that already converted.
  const upperType = (dataType as string).toUpperCase();
  return engine === 'mssql' && upperType.startsWith('N') && maxLength > 1
    ? Math.floor(maxLength / 2)
    : maxLength;
}
